/* import __COLOCATED_TEMPLATE__ from './user-selector.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import { MAX_USERNAME_LENGTH } from 'embercom/components/workflows/preview/preview-module';
import type AppService from 'embercom/services/app-service';

export interface Args {
  selectedUser?: UserSummary;
  asNewLead?: boolean;
  selectedAudiences?: string[];
  showAudiences?: boolean;
  onOpenUserSearch?: () => void;
  onSelectPreviewUser?: () => void;
  onOpenAudienceSearch: () => void;
  isOnProceduresPage: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class UserSelector extends Component<Signature> {
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare appService: AppService;

  get canUseAudiencesInPreview() {
    return this.appService.app.canUseAudiencesInPreview;
  }

  get isPreviewUserSelected() {
    return !this.args.selectedUser && !this.args.asNewLead && !this.isAudienceSelected;
  }

  get isSpecificUserSelected() {
    return (
      (Boolean(this.args.selectedUser) || Boolean(this.args.asNewLead)) && !this.isAudienceSelected
    );
  }

  get isAudienceSelected() {
    return !!this.args.selectedAudiences?.length;
  }

  private truncateString(text: string | undefined): string | undefined {
    if (!text) {
      return undefined;
    }

    if (text.length <= MAX_USERNAME_LENGTH) {
      return text;
    }

    return `${text.slice(0, MAX_USERNAME_LENGTH)}...`;
  }

  get truncatedUserName(): string | undefined {
    return this.truncateString(this.args.selectedUser?.displayAs);
  }

  get truncatedAudiencesName(): string | undefined {
    if (!this.args.selectedAudiences?.length) {
      return undefined;
    }

    let count = this.args.selectedAudiences.length;
    if (count === 1) {
      let segment = this.aiContentSegmentsService.segments.find(
        (seg) => seg.id === this.args.selectedAudiences![0],
      );
      let audienceName = segment?.nameWithFallback || this.args.selectedAudiences[0];
      return this.truncateString(audienceName);
    }
    return `${count} audiences`;
  }

  @action
  selectUserOrLead(): void {
    this.args.onOpenUserSearch?.();
  }

  @action
  selectPreviewUser(): void {
    this.args.onSelectPreviewUser?.();
  }

  @action
  selectAudience(): void {
    this.args.onOpenAudienceSearch();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Impersonation::UserSelector': typeof UserSelector;
    'ai-agent/impersonation/user-selector': typeof UserSelector;
  }
}
